import { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Paper, Button, Alert } from '@mui/material';
import { AppContext, SttTabs, SttFullDialog } from '../../../sporttia/all';
import MshipDetail from './MshipDetail';
import MshipPayments from './MshipPayments';
import MshipPurseMovs from './MshipPurseMovs';
import MshipBookings from './MshipBookings';
import MshipStudents from './MshipStudents';
import MshipEventParticipants from './MshipEventParticipants';
import MshipPrivileges from './MshipPrivileges';
import MshipGroups from './MshipGroups';
import MshipDocuments from './MshipDocuments';

type MshipData = {
    id: number;
    name?: string;
    surname1?: string;
    surname2?: string;
    identificationType?: string;
    dni?: string;
    gender?: string;
    notes?: string;
    birthday?: string;
    email?: string;
    email2?: string;
    mobile?: string;
    mobile2?: string;
    address?: string;
    addrVia?: string;
    addrType?: string;
    addrNumber?: string;
    addrLetter?: string;
    iban?: string | null;
    postalCode?: string;
    tutor?: string;
    city?: string;
    status?: string;
    user?: {
        id: number;
    };
};

type Props = {
    id: number;
    open: boolean;
    onClose: () => void;
    initialTab?: number;
    initialTabKey?: string;
    disableUrlNavigation?: boolean;
};

export default function Mship({
    id,
    open,
    onClose,
    initialTab,
    initialTabKey,
    disableUrlNavigation = false,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [mship, setMship] = useState<MshipData | undefined>();

    // Define tabs configuration with visibility
    const tabsConfig = useMemo(
        () => [
            { key: 'detail', show: true },
            { key: 'payments', show: !!mship },
            { key: 'purse', show: !!mship },
            { key: 'groups', show: !!mship },
            { key: 'bookings', show: !!mship },
            { key: 'activities', show: !!mship && !cxt.zero },
            { key: 'events', show: !!mship && !cxt.zero },
            { key: 'privileges', show: !!mship },
            { key: 'documents', show: !!mship },
        ],
        [mship, cxt.zero],
    );

    // Get only visible tabs
    const visibleTabKeys = useMemo(
        () => tabsConfig.filter((tab) => tab.show).map((tab) => tab.key),
        [tabsConfig],
    );

    // Calculate initial tab index from key or use provided index
    const computedInitialTab = useMemo(() => {
        if (initialTabKey) {
            const index = visibleTabKeys.indexOf(initialTabKey);
            return index >= 0 ? index : 0;
        }
        return initialTab ?? 0;
    }, [initialTabKey, initialTab, visibleTabKeys]);

    // Handle tab change - update URL only if not disabled
    const handleTabChange = (tabIndex: number) => {
        if (disableUrlNavigation) return;
        const tabKey = visibleTabKeys[tabIndex];
        if (tabKey && id) {
            navigate(`/mships/${id}/${tabKey}`, { replace: true });
        }
    };

    const loadMship = () => {
        cxt?.api('GET', `/mships/${id}`, {
            success: (r: any) => {
                setMship(r.mship);
            },
        });
    };

    useEffect(() => {
        if (id) {
            loadMship();
        }

        return () => {
            setTimeout(() => setMship(undefined), 50);
        };
    }, [id]);

    function handleChangeMship({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) {
        if (!name) return;
        if (!mship) return;

        switch (name) {
            case 'tutor':
                setMship({ ...mship, tutor: (value as string) || '' });
                break;
            case 'city.name':
                setMship({
                    ...mship,
                    city: value ? (value as { city: string }).city : '',
                });
                break;
            case 'iban':
                setMship({
                    ...mship,
                    iban: value === '' ? null : (value as string),
                });
                break;
            default:
                setMship({ ...mship, [name]: value });
        }
    }

    const restoreUser = () => {
        if (!mship) return;

        cxt?.api('PUT', `/mships/${mship.id}`, {
            params: {
                status: 'ACTIVE',
            },
            success: (r: any) => {
                setMship(r.mship);
                cxt?.showMessage('S', t('generic.recovered'));
            },
        });
    };

    return (
        <SttFullDialog
            open={open}
            onClose={onClose}
            title={[mship?.name, mship?.surname1, mship?.surname2]
                .filter(Boolean)
                .join(' ')}
            urlPath={!disableUrlNavigation && id ? `/mships/${id}` : undefined}
        >
            <Paper>
                {mship?.status === 'INACTIVE' && (
                    <Alert
                        severity="warning"
                        action={
                            <Button color="inherit" onClick={restoreUser}>
                                {t('generic.recover')}
                            </Button>
                        }
                    >
                        {t('mship.mshipDeleted')}
                    </Alert>
                )}

                <SttTabs
                    initialTab={computedInitialTab}
                    onTabChange={handleTabChange}
                    tabs={[
                        {
                            key: 'detail',
                            caption: t('generic.detail'),
                            component: (
                                <MshipDetail
                                    mship={mship || ({} as any)}
                                    onChange={handleChangeMship}
                                    setMship={setMship as any}
                                    onClose={onClose}
                                />
                            ),
                        },
                        {
                            key: 'payments',
                            caption: t('payment.payments'),
                            show: !!mship,
                            component: <MshipPayments mship={mship!} />,
                        },
                        {
                            key: 'purse',
                            caption: t('generic.purse'),
                            show: !!mship,
                            component: <MshipPurseMovs mship={mship!} />,
                        },
                        {
                            key: 'groups',
                            caption: t('generic.groups'),
                            show: !!mship,
                            component: <MshipGroups mship={mship as any} />,
                        },
                        {
                            key: 'bookings',
                            caption: t('generic.bookings'),
                            show: !!mship,
                            component: <MshipBookings mship={mship!} />,
                        },
                        {
                            key: 'activities',
                            caption: t('generic.activities'),
                            show: !!mship && !cxt.zero,
                            component: <MshipStudents mship={mship!} />,
                        },
                        {
                            key: 'events',
                            caption: t('generic.events'),
                            show: !!mship && !cxt.zero,
                            component: (
                                <MshipEventParticipants mship={mship!} />
                            ),
                        },
                        {
                            key: 'privileges',
                            caption: t('generic.privileges'),
                            show: !!mship,
                            component: <MshipPrivileges mship={mship!} />,
                        },
                        {
                            key: 'documents',
                            caption: t('generic.documents'),
                            show: !!mship,
                            component: <MshipDocuments mship={mship!} />,
                        },
                    ]}
                />
            </Paper>
        </SttFullDialog>
    );
}
